import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Menu, X, User, LogOut, ShoppingCart, Phone } from 'lucide-react'
import SearchAutocompleteBox from '../product/SearchAutocompleteBox'
import type { Suggestion } from '../product/SearchAutocompleteBox'
import logoSvg from '../../assets/gcg-logo.svg'
import { Button } from '../ui/button'
import { useAuth } from '../../context/AuthContext'
import { useCart } from '../../context/CartContext'
import LanguageSwitcher from './LanguageSwitcher'
import { useLocalePath } from './LanguageRoute'
import { fetchNavigation } from '../../lib/cms'

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const urlSearch = new URLSearchParams(location.search).get('search') ?? ''
  const [searchQuery, setSearchQuery] = useState(urlSearch)
  const { t } = useTranslation()
  const { user, isAuthenticated, isLoading: authLoading, login, logout } = useAuth()
  const { itemCount } = useCart()
  const lp = useLocalePath()

  const currentLang = lp('/').replace(/\//g, '') as 'nl' | 'en'

  const { data: cmsNav } = useQuery({
    queryKey: ['cms-navigation', currentLang],
    queryFn: () => fetchNavigation({ locale: currentLang }),
    staleTime: 10 * 60 * 1000,
    retry: false,
  })

  const HARDCODED_NAV = [
    { label: t('nav.products'), href: lp('/products') },
    { label: t('nav.about'), href: lp('/about') },
    { label: t('nav.contact'), href: lp('/contact') },
  ]

  const NAV_LINKS = cmsNav?.mainNav?.length
    ? cmsNav.mainNav.map(item => ({ label: item.label, href: lp(item.href) }))
    : HARDCODED_NAV

  useEffect(() => {
    setSearchQuery(urlSearch)
  }, [urlSearch])

  function handleSelectSuggestion(s: Suggestion) {
    navigate(lp(`/product/${s.slug}`))
    setMobileOpen(false)
  }

  function isActive(href: string) {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  return (
    <header className="bg-white border-b border-gcg-border sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main bar */}
        <div className="flex items-center justify-between h-[72px] gap-4">
          {/* Logo */}
          <Link to={lp('/')} className="flex items-center gap-2 shrink-0">
            <img src={logoSvg} alt="Global Control Group" className="h-8 w-auto" />
            <div className="flex items-baseline gap-1.5">
              <span className="text-[15px] font-bold text-gcg-text leading-tight tracking-[-0.01em]">
                <span className="sm:hidden">GCG</span>
                <span className="hidden sm:inline">Global Control Group</span>
              </span>
              <span className="text-[15px] font-extrabold text-gcg-orange leading-tight tracking-[-0.01em]">Store</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium transition-colors relative ${
                  isActive(link.href)
                    ? 'text-primary after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-primary after:rounded-full'
                    : 'text-gcg-text hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <div className="hidden md:flex items-center flex-1 max-w-md">
            <SearchAutocompleteBox
              value={searchQuery}
              onChange={setSearchQuery}
              onSubmit={q => { if (q.trim()) navigate(`${lp('/products')}?search=${encodeURIComponent(q.trim())}`) }}
              onSelectSuggestion={handleSelectSuggestion}
              placeholder={t('product.search')}
              inputClassName="w-full bg-neutral-50 border border-gcg-border rounded-md pl-8 pr-8 py-1.5 text-sm text-gcg-text placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Cart + CTA + Language */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher />
            <Link
              to={lp('/cart')}
              className="relative p-2 text-gcg-text hover:text-primary transition-colors"
              aria-label={t('nav.cart')}
            >
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold leading-none">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>
            {/* Reserve a fixed slot so the auth UI doesn't shift the cart/help icons
                when the session resolves. The logged-in variant (name button + logout)
                is the widest case (~10rem); reserve that and right-align the skeleton. */}
            <div className="min-w-[10rem] h-8 flex items-center justify-end">
              {authLoading ? (
                <div className="w-24 h-8 rounded-md bg-neutral-100 animate-pulse" />
              ) : isAuthenticated ? (
                <div className="flex items-center gap-1">
                  <Button variant="secondary" size="sm" asChild>
                    <Link to={lp('/account')}>
                      <User className="h-4 w-4" />
                      {user?.first_name || t('nav.account')}
                    </Link>
                  </Button>
                  <button
                    onClick={() => logout()}
                    className="p-2 text-neutral-400 hover:text-primary transition-colors"
                    aria-label={t('nav.logout')}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => login()}
                >
                  <User className="h-4 w-4" />
                  {t('nav.login')}
                </Button>
              )}
            </div>
          </div>

          {/* Mobile-only: cart + auth icons */}
          <div className="flex md:hidden items-center gap-1">
            <Link
              to={lp('/cart')}
              className="relative p-2 text-gcg-text hover:text-primary transition-colors"
              aria-label={t('nav.cart')}
            >
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold leading-none">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>
            {/* Reserve the auth-icon slot even while loading so the hamburger doesn't shift. */}
            <div className="w-9 h-8 flex items-center justify-center">
              {!authLoading && (
                isAuthenticated ? (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={lp('/account')} aria-label={t('nav.account')}>
                      <User className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => login()} aria-label={t('nav.login')}>
                    <User className="h-4 w-4" />
                  </Button>
                )
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-3 rounded-md text-gcg-text hover:bg-gcg-tint"
            onClick={() => setMobileOpen(prev => !prev)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Trust bar */}
      <div className="border-t border-gcg-border bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="flex items-center justify-start gap-6 sm:gap-10 py-1.5 overflow-x-auto whitespace-nowrap text-xs text-gcg-text [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-pl-4" tabIndex={0}>
            {(['shop.trustBar.item1', 'shop.trustBar.item2', 'shop.trustBar.item3'] as const).map(key => (
              <li key={key} className="flex items-center gap-2 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
                {t(key)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gcg-border px-4 pb-4">
          <div className="pt-3 mb-2">
            <SearchAutocompleteBox
              value={searchQuery}
              onChange={setSearchQuery}
              onSubmit={q => { if (q.trim()) { navigate(`${lp('/products')}?search=${encodeURIComponent(q.trim())}`); setMobileOpen(false) } }}
              onSelectSuggestion={handleSelectSuggestion}
              placeholder={t('product.search')}
              inputClassName="w-full bg-neutral-50 border border-gcg-border rounded-md pl-8 pr-8 py-2 text-sm text-gcg-text placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.href)
                    ? 'text-primary bg-gcg-tint'
                    : 'text-gcg-text hover:text-primary hover:bg-gcg-tint'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:+31299705078"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gcg-text hover:text-primary"
            >
              <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
              <span>+31&nbsp;299&nbsp;705&nbsp;078</span>
            </a>
            <div className="flex items-center mt-2 pt-2 border-t border-gcg-border">
              <LanguageSwitcher />
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
